import { supabase } from '../lib/supabase.js';
import { setState } from '../state/store.js';
import { isOnline, OFFLINE_SAVE_MESSAGE } from '../lib/onlineGuard.js';

export async function loadLabourForSite(siteId) {
  if (!siteId) {
    setState({ labourRecords: [] });
    return;
  }
  setState({ labourRecordsLoading: true });
  const { data, error } = await supabase
    .from('labour_payments')
    .select('id, payment_date, worker_name, basis, days, daily_rate, sqft, sqft_rate, contract_amount, amount_paid, payment_type, payment_mode, notes, created_at')
    .eq('site_id', siteId)
    .order('payment_date', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Failed to load labour payments', error);
    setState({ labourRecords: [], labourRecordsLoading: false });
    return;
  }
  setState({ labourRecords: data || [], labourRecordsLoading: false });
}

export function computeLabourAmount(basis, values) {
  if (basis === 'daily') return (Number(values.days) || 0) * (Number(values.daily_rate) || 0);
  if (basis === 'sqft') return (Number(values.sqft) || 0) * (Number(values.sqft_rate) || 0);
  if (basis === 'contract') return Number(values.contract_amount) || 0;
  return 0;
}

/**
 * fields: { site_id, payment_date, worker_name, basis, days, daily_rate, sqft, sqft_rate,
 *           contract_amount, payment_mode, notes }
 * Calls the EXISTING create_labour_payment_atomic RPC -- does not insert into
 * labour_payments/transactions directly. Returns { ok, error }.
 */
export async function saveLabour(fields) {
  if (!isOnline()) return { ok: false, error: OFFLINE_SAVE_MESSAGE };
  if (!fields.site_id) return { ok: false, error: 'No site selected' };
  if (!fields.worker_name || !fields.worker_name.trim()) return { ok: false, error: 'Worker is required' };
  if (!['daily', 'sqft', 'contract'].includes(fields.basis)) return { ok: false, error: 'Invalid payment basis' };

  let days = null, daily_rate = null, sqft = null, sqft_rate = null, contract_amount = null;

  if (fields.basis === 'daily') {
    days = Number(fields.days);
    daily_rate = Number(fields.daily_rate);
    if (!days || days <= 0) return { ok: false, error: 'Enter valid days (> 0)' };
    if (fields.daily_rate === '' || isNaN(daily_rate) || daily_rate < 0) return { ok: false, error: 'Enter a valid daily rate (>= 0)' };
  } else if (fields.basis === 'sqft') {
    sqft = Number(fields.sqft);
    sqft_rate = Number(fields.sqft_rate);
    if (!sqft || sqft <= 0) return { ok: false, error: 'Enter valid sqft (> 0)' };
    if (fields.sqft_rate === '' || isNaN(sqft_rate) || sqft_rate < 0) return { ok: false, error: 'Enter a valid rate/sqft (>= 0)' };
  } else if (fields.basis === 'contract') {
    contract_amount = Number(fields.contract_amount);
    if (!contract_amount || contract_amount <= 0) return { ok: false, error: 'Enter a valid contract amount (> 0)' };
  }

  const amount = computeLabourAmount(fields.basis, { days, daily_rate, sqft, sqft_rate, contract_amount });
  if (!amount || amount <= 0) return { ok: false, error: 'Calculated amount must be greater than 0' };

  const { error } = await supabase.rpc('create_labour_payment_atomic', {
    p_site_id: fields.site_id,
    p_payment_date: fields.payment_date,
    p_worker_name: fields.worker_name.trim(),
    p_basis: fields.basis,
    p_days: days,
    p_daily_rate: daily_rate,
    p_sqft: sqft,
    p_sqft_rate: sqft_rate,
    p_contract_amount: contract_amount,
    p_amount_paid: amount,
    p_payment_type: 'labour payment',
    p_payment_mode: fields.payment_mode || null,
    p_notes: fields.notes || null
  });

  if (error) return { ok: false, error: error.message };
  return { ok: true };
}
